export interface Project {
  id: string
  name: string
  description?: string
  path: string
  url: string
  info?: any
  data?: any // Add data property to store the Info.json content
  status?: 'completed' | 'ongoing' // Asset (completed) or Project (ongoing)
}

export interface ProjectData {
  Lines: any
  [key: string]: any
}

const projectService = {
  async getProjectById(projectId: string, apiEndpoint: string): Promise<Project | null> {
    try {
      // Looking for project

      // Normalize the project ID for comparison (both with spaces and underscores)
      const normalizedId = projectId.trim()
      const idWithUnderscores = normalizedId.replace(/ /g, '_')

      // Try to fetch from assets first
      const assetsUrl = `${apiEndpoint}assets`
      // Trying assets endpoint
      const assetsResponse = await fetch(assetsUrl)

      if (assetsResponse.ok) {
        const assetsData = await assetsResponse.json()

        if (assetsData.assets && Array.isArray(assetsData.assets)) {
          // Find the project with the matching ID in assets (try both formats)
          const asset = assetsData.assets.find((a: any) =>
            a.name === normalizedId ||
            a.name === idWithUnderscores ||
            a.name.replace(/_/g, ' ') === normalizedId
          )

          if (asset) {
            // Found in assets
            return {
              id: asset.name,
              name: asset.name,
              description: asset.info?.description || `Asset from ${apiEndpoint}`,
              path: asset.path,
              url: asset.url,
              info: asset.info,
              status: 'completed',
            }
          }
        }
      }

      // If not found in assets, try projects
      const projectsUrl = `${apiEndpoint}projects`
      // Not found in assets, trying projects endpoint
      const projectsResponse = await fetch(projectsUrl)

      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json()

        if (projectsData.projects && Array.isArray(projectsData.projects)) {
          // Find the project with the matching ID in projects (try both formats)
          const project = projectsData.projects.find((p: any) =>
            p.name === normalizedId ||
            p.name === idWithUnderscores ||
            p.name.replace(/_/g, ' ') === normalizedId
          )

          if (project) {
            // Found in projects
            return {
              id: project.name,
              name: project.name,
              description: project.info?.description || `Project from ${apiEndpoint}`,
              path: project.path,
              url: project.url,
              info: project.info,
              status: 'ongoing',
            }
          }
        }
      }

      // Not found in either endpoint
      // Project not found in either assets or projects
      return null
    } catch (error: any) {
      // Error fetching project
      throw new Error(`Failed to fetch project: ${error.message}`)
    }
  },

  async fetchProjectData(project: Project, apiEndpoint: string): Promise<ProjectData | null> {
    try {
      // First, get the list of files and directories for this project
      const response = await fetch(project.url)

      if (!response.ok) {
        throw new Error(`Failed to fetch project contents: ${response.statusText}`)
      }

      const directoryData = await response.json()

      // Find the Info.json file in the files list
      const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

      if (!infoFile) {
        throw new Error("Info.json file not found in project")
      }

      // Fetch the Info.json file content
      const infoResponse = await fetch(infoFile.url)

      if (!infoResponse.ok) {
        throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
      }

      const infoData = await infoResponse.json()

      if (!infoData || !infoData.Lines) {
        throw new Error("Invalid project data format - missing Lines property")
      }

      return infoData as ProjectData
    } catch (error: any) {
      // Error fetching project data
      return null
    }
  },

  async getProjects(apiEndpoint: string): Promise<Project[]> {
    try {
      // Fetch the list of assets from the API
      const response = await fetch(`${apiEndpoint}assets`)

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.assets || !Array.isArray(data.assets)) {
        throw new Error("API returned invalid data format")
      }

      // Map the assets to our Project interface
      return data.assets.map((asset: any) => ({
        id: asset.name,
        name: asset.name,
        description: asset.info?.description || `Asset from ${apiEndpoint}`,
        path: asset.path,
        url: asset.url,
        info: asset.info,
        status: 'completed' as const,
      }))
    } catch (error: any) {
      // Error fetching projects
      throw new Error(`Failed to fetch projects: ${error.message}`)
    }
  },

  async getAssets(apiEndpoint: string): Promise<Project[]> {
    try {
      const url = `${apiEndpoint}assets`
      // Fetching from URL

      // Fetch the list of assets (completed projects) from the API
      const response = await fetch(url)

      // Response received

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.assets || !Array.isArray(data.assets)) {
        throw new Error("API returned invalid data format")
      }

      // Map the assets to our Project interface with completed status
      return data.assets.map((asset: any) => ({
        id: asset.name,
        name: asset.name,
        description: asset.info?.description || `Asset from ${apiEndpoint}`,
        path: asset.path,
        url: asset.url,
        info: asset.info,
        status: 'completed' as const,
      }))
    } catch (error: any) {
      // Error fetching assets
      throw new Error(`Failed to fetch assets: ${error.message}`)
    }
  },

  async getOngoingProjects(apiEndpoint: string): Promise<Project[]> {
    try {
      const url = `${apiEndpoint}projects`
      // Fetching from URL

      // Fetch the list of ongoing projects from the API
      const response = await fetch(url)

      // Response received

      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.projects || !Array.isArray(data.projects)) {
        throw new Error("API returned invalid data format")
      }

      // Map the projects to our Project interface with ongoing status
      return data.projects.map((project: any) => ({
        id: project.name,
        name: project.name,
        description: project.info?.description || `Project from ${apiEndpoint}`,
        path: project.path,
        url: project.url,
        info: project.info,
        status: 'ongoing' as const,
      }))
    } catch (error: any) {
      // Error fetching ongoing projects
      throw new Error(`Failed to fetch ongoing projects: ${error.message}`)
    }
  },

  async getProjectWithData(project: Project, apiEndpoint: string): Promise<Project | null> {
    try {
      // First, get the list of files and directories for this project
      const response = await fetch(project.url)

      if (!response.ok) {
        throw new Error(`Failed to fetch project contents: ${response.statusText}`)
      }

      const directoryData = await response.json()

      // Find the Info.json file in the files list
      const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

      if (!infoFile) {
        throw new Error("Info.json file not found in project")
      }

      // Fetch the Info.json file content
      const infoResponse = await fetch(infoFile.url)

      if (!infoResponse.ok) {
        throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
      }

      const infoData = await infoResponse.json()

      // Return the project with the data
      return {
        ...project,
        data: infoData,
      }
    } catch (error: any) {
      // Error fetching project with data
      return null
    }
  },
}

export default projectService

